import { useQuery } from "@tanstack/react-query";
import { router } from "expo-router";
import { useField, useForm } from "mobx-easy-form";
import { Observer, observer } from "mobx-react";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { StyleSheet } from "react-native";
import { z } from "zod";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { MultilineTextInput } from "~/components/V2/MultilineTextInput";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { assert } from "~/utils/assert";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

const S = StyleSheet.create({
  multilineTextInput: {
    height: 180,
    padding: styleConstantsV2.spacing16,
    paddingBottom: styleConstantsV2.spacing32,
    fontFamily: "DMSans-Regular",
    fontSize: styleConstantsV2.fontSizeBodyLarge,
    lineHeight: 24,
    borderWidth: 1,
    borderRadius: styleConstantsV2.borderRadius8,
  },
  textLengthCounter: {
    position: "absolute",
    right: styleConstantsV2.spacing8,
    bottom: styleConstantsV2.spacing8,
  },
  errorText: { color: styleConstantsV2.colorError },
  nextButtonContainer: { paddingBottom: 64, paddingTop: 32 },
});

const NotesScreen = observer(function NotesScreen() {
  const { clinicId, isStandAloneBookingFlow, isNewUser } =
    useStandaloneBookingFlow();
  useProgressBarStep(6);

  const store = useStore();
  const activeUser = store.authStore.activeUser;
  const intl = useIntl();

  const selectedReasonId = store.bookingStore.reasonId;
  const selectedClinicId = store.bookingStore.clinicId;

  assert(
    selectedReasonId,
    "Reason ID is not set, can not get reason information",
  );
  assert(
    selectedClinicId,
    "Clinic ID is not set, can not get reason information",
  );

  const {
    data: reason,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["reason", selectedReasonId, selectedClinicId],
    queryFn: async () => {
      const reason = await store.appointmentReasonStore.getAppointmentReason(
        selectedReasonId,
        selectedClinicId,
      );
      return reason;
    },
  });

  const form = useForm({
    onSubmit: async ({ values }) => {
      store.bookingStore.setClientNotes(values.notes);

      if (isStandAloneBookingFlow) {
        // Log the event to the booking data layer
        bookingDataLayerPush({
          event: "Booking_web_app",
          ga_action: "Choose notes",
          ga_label: values.notes ? "note taken" : "no note taken",
        });

        if (isNewUser) {
          router.navigate({
            pathname: "/(standalone)/booking/add-client-details",
            params: {
              clinicId,
              isNewUser: "1",
            },
          });
        } else {
          // Check if existing user has email address
          // If not, send him to 'add-client-details' screen
          // otherwise, go to overview
          if (activeUser?.email && isNewUser === false) {
            router.navigate({
              pathname: "/(standalone)/booking/overview",
              params: {
                clinicId,
                isNewUser: "0",
              },
            });
          } else {
            router.navigate({
              pathname: "/(standalone)/booking/add-existing-client-details",
              params: {
                clinicId,
                isNewUser: "0",
              },
            });
          }
        }
      } else {
        router.navigate({
          pathname: "/(app)/book-appointment/overview",
        });
      }
    },
  });

  const notes = useField(
    {
      id: "notes",
      form,
      initialValue: "",
      validate: (value) => {
        const parsedValue = z.string().parse(value);

        if (reason?.clientNotesRequired && parsedValue.length === 0) {
          return {
            error: intl.formatMessage({
              id: "cPbREX",
              defaultMessage: "Please fill in the notes",
              description: "Error message when notes are empty",
            }),
            parsed: undefined,
          };
        }
        return { error: undefined, parsed: value };
      },
    },
    [reason?.clientNotesRequired],
  );

  if (isLoading) {
    return (
      <View flex colorWhite>
        <View padding32 centerContent>
          <SpinnerV2 />
        </View>
      </View>
    );
  }

  if (isError) {
    return (
      <View flex colorWhite>
        <View centerContent paddingHorizontal24>
          <ErrorViewV2
            error={error}
            title={intl.formatMessage({
              id: "m4dnTD",
              defaultMessage: "Something went wrong",
              description:
                "Error message title when fetching clinic list fails",
            })}
          />
          <ButtonV2
            onPress={refetch}
            title={intl.formatMessage({
              id: "ghblYR",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching clinic list",
            })}
          />
        </View>
      </View>
    );
  }

  return (
    <>
      <View paddingVertical40 paddingHorizontal16 colorWhite>
        <BrandText sizeH1 weightExtraBold alignCenter colorBlack>
          <FormattedMessage
            defaultMessage="Notes for the clinic"
            id="/vhYnT"
            description="Title for notes section"
          />
        </BrandText>
        <Spacer medium />
        <BodyText alignCenter sizeLarge weightRegular colorBlack>
          <FormattedMessage
            defaultMessage="Please indicate symptoms, wishes or anything else that is good for us to know, e.g. if the animal has visited us before with another animal owner."
            id="S2gCCL"
            description="Description for notes section"
          />
        </BodyText>
        <Spacer extraLarge />
        <Observer>
          {() => {
            const isError = form.computed.isError && form.state.submitCount > 0;
            return (
              <MultilineTextInput
                value={notes.state.value}
                onChangeText={(text) => {
                  notes.actions.onChange(text);
                }}
                maxLength={500}
                placeholder={
                  reason?.clientNotesRequired
                    ? intl.formatMessage({
                        id: "wAgZ+4",
                        defaultMessage: "(Mandatory)",
                        description: `Notes multiline input placeholder`,
                      })
                    : intl.formatMessage({
                        id: "t9egKK",
                        defaultMessage: "(Optional)",
                        description: `Notes multiline input placeholder`,
                      })
                }
                isError={isError}
                helperText={
                  isError ? (
                    <BodyText sizeMedium alignCenter style={S.errorText}>
                      {notes.computed.error}
                    </BodyText>
                  ) : undefined
                }
              />
            );
          }}
        </Observer>
      </View>
      <View
        flex
        flexDirectionColumnReverse
        paddingHorizontal24
        colorWhite
        style={S.nextButtonContainer}
      >
        <Observer>
          {() => {
            return (
              <ButtonV2
                medium
                title={intl.formatMessage({
                  id: "3Wcqsy",
                  defaultMessage: "Next",
                  description: "Next button",
                })}
                onPress={form.actions.submit}
                disabled={form.computed.isError && form.state.submitCount > 0}
              />
            );
          }}
        </Observer>
      </View>
    </>
  );
});

// If any required data from earlier in the booking process is missing, redirect to the first step
export const NotesScreenWithBookingData = withBookingData(NotesScreen, [
  "clinicId",
  "patientId",
  "reasonId",
  "practitionerId",
  "start",
  "end",
]);
